import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MeetingsStatisticsAPI from '../../../api/MeetingsStatisticsAPI';
import LinearGraph from '../../../components/LinearGraph/LinearGraph';
import NoData from '../../../components/NoData/NoData';
import { bucketizeDailySeriesForChart } from '../../../shared/bucketizeDailySeriesForChart';

type Props = {
  timeRange: { start: string; end: string };
};

function MeetingsTotalOverTime({ timeRange }: Props) {
  const { t, i18n } = useTranslation();
  const [dailyLabels, setDailyLabels] = useState<string[]>([]);
  const [dailyNotCancelled, setDailyNotCancelled] = useState<number[]>([]);
  const [dailyBrideCancelled, setDailyBrideCancelled] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await MeetingsStatisticsAPI.getMeetingsTotalOverTime({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        const rawLabels = res.data?.labels || [];
        setDailyLabels(rawLabels.map((l) => String(l).slice(0, 10)));
        setDailyNotCancelled((res.data?.notBrideCancelledValues || []).map((v) => Number(v)));
        setDailyBrideCancelled((res.data?.brideCancelledValues || []).map((v) => Number(v)));
      } catch {
        setDailyLabels([]);
        setDailyNotCancelled([]);
        setDailyBrideCancelled([]);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const { xLabels, valuesNot, valuesBride, total } = useMemo(() => {
    const rangeStart = new Date(timeRange.start);
    const rangeEnd = new Date(timeRange.end);
    const { xLabels: xl, values: vNot } = bucketizeDailySeriesForChart(
      rangeStart,
      rangeEnd,
      dailyLabels,
      dailyNotCancelled,
      i18n.language,
    );
    const { values: vBride } = bucketizeDailySeriesForChart(
      rangeStart,
      rangeEnd,
      dailyLabels,
      dailyBrideCancelled,
      i18n.language,
    );
    const sumNot = vNot.reduce((a, b) => a + b, 0);
    const sumBride = vBride.reduce((a, b) => a + b, 0);
    return { xLabels: xl, valuesNot: vNot, valuesBride: vBride, total: sumNot + sumBride };
  }, [timeRange.start, timeRange.end, dailyLabels, dailyNotCancelled, dailyBrideCancelled, i18n.language]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('stats.meetings_total_over_time_description')}</p>
      {total === 0 ? (
        <NoData message={t('global.empty_results')} />
      ) : (
        <LinearGraph
          title={t('stats.meetings_total_over_time') || 'Meetings over time'}
          xLabels={xLabels}
          showAllXTicks
          dataSets={[
            {
              label: t('stats.meetings_total_over_time_not_bride_cancelled'),
              data: valuesNot,
              borderColor: 'rgba(99, 102, 241, 0.9)',
              backgroundColor: 'rgba(99, 102, 241, 0.2)',
              borderWidth: 2,
            },
            {
              label: t('stats.meetings_total_over_time_bride_cancelled'),
              data: valuesBride,
              borderColor: 'rgba(244, 63, 94, 0.9)',
              backgroundColor: 'rgba(244, 63, 94, 0.15)',
              borderWidth: 2,
            },
          ]}
        />
      )}
    </div>
  );
}

export default MeetingsTotalOverTime;
